import React, { useState } from "react";
import { Download, Loader2, Mail, Eye, Sparkles, CheckCircle2 } from "lucide-react";
import { API, previewPdfUrl } from "../lib/api";
import axios from "axios";

/**
 * FREE illness-protocol PDF download — used as a lead magnet.
 * Customer can optionally leave an email (helps you build the newsletter list)
 * then downloads immediately. No Stripe involved.
 */
const PdfCheckoutCard = ({ illnessId, illnessName }) => {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [downloadUrl, setDownloadUrl] = useState("");

  const handleFreeDownload = async () => {
    setError(""); setLoading(true);
    try {
      const res = await axios.post(`${API}/free-download/illness-pdf`, {
        illness_id: illnessId,
        email: email.trim() || null,
        full_name: fullName.trim() || null,
      });
      const url = `${API}/download/illness-pdf/${res.data.download_token}`;
      setDownloadUrl(url);
      // auto-trigger the actual file download
      window.location.href = url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Download failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="pdf-checkout-card"
      className="rounded-3xl bg-gradient-to-br from-forest via-forest to-sage text-sand p-7 sm:p-8 relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-islamic-pattern opacity-10 pointer-events-none" />
      <div className="relative">
        <div className="inline-flex items-center gap-1.5 bg-gold/20 text-gold rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.25em] mb-3">
          <Sparkles className="w-3 h-3" /> Free download · No payment
        </div>
        <h3 className="font-heading text-2xl font-semibold leading-tight mb-2">
          Get the {illnessName} Protocol PDF
        </h3>
        <p className="text-sm text-sand/85 leading-relaxed mb-4">
          A printable, fully-referenced summary — Quranic verses, Sahih Hadiths, Hijama points,
          prophetic remedies and modern studies. Yours, completely free.
        </p>

        <div className="bg-sand/10 rounded-2xl p-4 mb-4 text-xs text-sand/85 leading-relaxed">
          <span className="text-gold">Optional:</span> leave your email so we can send you new
          Sunnah health protocols as we publish them. Never spam, easy to unsubscribe.
        </div>

        {!downloadUrl && (
          <div className="space-y-2 mb-4">
            <input
              data-testid="free-pdf-name"
              type="text"
              placeholder="Your name (optional)"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full rounded-xl bg-sand/10 border border-sand/20 px-4 py-2.5 text-sm placeholder-sand/50 outline-none focus:bg-sand/20 focus:border-gold/60"
            />
            <input
              data-testid="free-pdf-email"
              type="email"
              placeholder="Your email (optional)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl bg-sand/10 border border-sand/20 px-4 py-2.5 text-sm placeholder-sand/50 outline-none focus:bg-sand/20 focus:border-gold/60"
            />
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            data-testid="free-pdf-download"
            onClick={handleFreeDownload}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 bg-gold text-forest hover:bg-sand rounded-full px-6 py-3 text-sm font-semibold transition-all hover:-translate-y-0.5 shadow-lg shadow-black/20 disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> :
              downloadUrl ? <CheckCircle2 className="w-4 h-4" /> : <Download className="w-4 h-4" />}
            {downloadUrl ? "Downloaded — get it again" : "Download free PDF"}
          </button>
          <a
            href={previewPdfUrl(illnessId)}
            target="_blank"
            rel="noreferrer"
            data-testid="preview-pdf-link"
            className="inline-flex items-center justify-center gap-2 bg-sand/15 hover:bg-sand/25 text-sand rounded-full px-5 py-3 text-sm font-medium transition"
          >
            <Eye className="w-4 h-4" /> Preview
          </a>
        </div>
        {downloadUrl && (
          <p className="text-[10px] text-sand/70 mt-3">
            If the file didn't open automatically, <a href={downloadUrl} className="underline">click here</a> to download again.
          </p>
        )}

        {error && (
          <p data-testid="free-pdf-error" className="text-xs text-sand bg-clay/40 rounded-lg px-3 py-2 mt-3">
            {error}
          </p>
        )}
      </div>
    </div>
  );
};

export default PdfCheckoutCard;
